import {
  Button,
  Divider,
  Dropdown,
  Flex,
  Layout,
  Menu,
  Tag,
  Tooltip,
  Typography
} from 'antd'
import { BookOpen, ChevronDown, ExternalLink, Globe } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

import type { CurrentAccountUser, OrganizationLong } from 'src/api/main'
import { AvatarWithInitials } from 'src/components/AvatarWithInitials'
import { DOCS_ORIGIN } from 'src/constants'
import { Tickets } from 'src/features/tickets'
import { paths } from 'src/router'
import { buildOrganizationUrl } from 'src/utils/postLoginRedirect'

import { useUserDropdownItems } from './hooks'
import { useStyles } from './styles'
import { resolveActiveWorkspace } from './utils'

const { Header } = Layout
const { Text } = Typography

type Props = {
  user: CurrentAccountUser | undefined
  workspaces: OrganizationLong[]
  activeWorkspaceId: number | undefined
  onSignOut: () => void
}

export const Topbar = ({
  user,
  workspaces,
  activeWorkspaceId,
  onSignOut
}: Props) => {
  const { styles } = useStyles()
  const { t } = useTranslation()
  const navigate = useNavigate()

  const [userMenuOpen, setUserMenuOpen] = useState(false)
  const goAccount = () => navigate(paths.account.profile)

  const userMenuItems = useUserDropdownItems(goAccount, onSignOut)

  const u = user?.user
  const fullName = [u?.first_name, u?.last_name]
    .filter(Boolean)
    .join(' ')
    .trim()
  const displayName =
    fullName || u?.email?.split('@')[0] || t('shell.user.fallback')
  const photoSrc = u?.photo?.file ?? undefined

  const activeWorkspace = resolveActiveWorkspace(workspaces, activeWorkspaceId)
  const workspaceRole = activeWorkspace?.access_role
  const workspaceHref = activeWorkspace?.domain_name
    ? buildOrganizationUrl(activeWorkspace.domain_name)
    : null
  const workspaceHost = workspaceHref ? new URL(workspaceHref).host : null

  return (
    <Header className={styles.topbar}>
      <Flex
        align="center"
        justify="flex-end"
        gap="middle"
        style={{ height: '100%' }}
      >
        <Flex align="center" gap={8}>
          {workspaceHref && workspaceHost ? (
            <Tooltip
              title={t('shell.topbar.openWorkspace', { url: workspaceHost })}
            >
              <a
                href={workspaceHref}
                target="_blank"
                rel="noreferrer noopener"
                className={styles.topbarWorkspaceChip}
                aria-label={t('shell.topbar.openWorkspace', {
                  url: workspaceHost
                })}
              >
                <Globe size={13} aria-hidden />
                <span className={styles.topbarWorkspaceChipUrl}>
                  {workspaceHost}
                </span>
                <ExternalLink
                  size={12}
                  aria-hidden
                  color="var(--ant-color-text-tertiary)"
                />
              </a>
            </Tooltip>
          ) : null}
          <a
            href={DOCS_ORIGIN}
            target="_blank"
            rel="noreferrer noopener"
            className={styles.topbarDocsLink}
          >
            <BookOpen size={14} aria-hidden />
            <span>{t('shell.topbar.docs')}</span>
          </a>
          <div className={styles.topbarDividerY} />
          <Tickets />
          {/* Language is chosen in profile / workspace settings, not from the
              header (TS-CORE-027). Appearance lives in the account menu. */}
          <div className={styles.topbarDividerY} />
          <Dropdown
            trigger={['click']}
            placement="bottomRight"
            open={userMenuOpen}
            onOpenChange={setUserMenuOpen}
            popupRender={() => (
              <div className={styles.topbarUserMenu}>
                <div className={styles.topbarUserMenuHeader}>
                  <Flex align="center" gap={12}>
                    <AvatarWithInitials
                      name={fullName || displayName}
                      id={u?.id}
                      size={36}
                      src={photoSrc}
                    />
                    <Flex vertical style={{ minWidth: 0, lineHeight: 1.25 }}>
                      <Flex align="center" gap={6} wrap="wrap">
                        <Text strong style={{ fontSize: 13 }}>
                          {displayName}
                        </Text>
                        {workspaceRole ? (
                          <Tag
                            color="processing"
                            style={{
                              marginInlineEnd: 0,
                              fontSize: 10,
                              lineHeight: '16px',
                              textTransform: 'capitalize'
                            }}
                          >
                            {t(`users.roles.${workspaceRole}`, workspaceRole)}
                          </Tag>
                        ) : null}
                      </Flex>
                      {u?.email ? (
                        <Text
                          type="secondary"
                          style={{ fontSize: 12 }}
                          ellipsis={{ tooltip: u.email }}
                        >
                          {u.email}
                        </Text>
                      ) : null}
                    </Flex>
                  </Flex>
                </div>
                <Divider style={{ margin: 0 }} />
                <Menu
                  items={userMenuItems}
                  selectable={false}
                  onClick={() => setUserMenuOpen(false)}
                  style={{ border: 'none', padding: 4 }}
                />
              </div>
            )}
          >
            <Button
              type="text"
              size="small"
              className={styles.topbarUser}
              data-shell-chrome="profile"
            >
              <Flex align="center" gap={8}>
                <AvatarWithInitials
                  name={fullName || displayName}
                  id={u?.id}
                  size={24}
                  src={photoSrc}
                />
                <span className={styles.topbarUserName} title={displayName}>
                  {displayName}
                </span>

                <ChevronDown
                  size={12}
                  color="var(--ant-color-text-secondary)"
                />
              </Flex>
            </Button>
          </Dropdown>
        </Flex>
      </Flex>
    </Header>
  )
}
